import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, RotateCcw, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../../context/DataContext";
import { burst, vibrate } from "../../lib/fx";
import { sfx } from "../../lib/sound";
import { MASCOT_CHEER } from "../../lib/tones";
import type { SharePayload } from "../../lib/share";
import type { RevealSpec } from "../../types";
import { PopButton } from "../PopButton";
import { CoinFlip } from "./CoinFlip";
import { DiceRoll } from "./DiceRoll";
import { PlayingCard } from "./PlayingCard";
import { SlotReel } from "./SlotReel";
import { PrizeWheel } from "./PrizeWheel";
import { ShareDialog } from "./ShareDialog";
import { RafflePodium } from "./RafflePodium";
import { CountdownDots, SaysResult, StageHeader } from "../ua/Stage";

const STAGE_BG: Record<string, string> = { coin: "bg-[#1a1006]", dice: "bg-[#0b1430]", card: "bg-[#2b0710]", wheel: "bg-[#22093f]", slot: "bg-[#1a1033]" };

interface Props {
  spec: RevealSpec | null;
  onAgain: () => void;
  onClose: () => void;
  againLabel?: string;
}

export const resultSize = (s: string) =>
  s.length <= 6 ? "text-6xl sm:text-7xl" : s.length <= 14 ? "text-5xl sm:text-6xl" : s.length <= 28 ? "text-4xl sm:text-5xl" : s.length <= 60 ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl";

function RevealAnimation({ spec, revealed, fast }: { spec: RevealSpec; revealed: boolean; fast: boolean }) {
  if (spec.anim === "coin") return <CoinFlip value={spec.animValue} fast={fast} />;
  if (spec.anim === "dice") return <DiceRoll values={spec.animValue} fast={fast} />;
  if (spec.anim === "card") return <PlayingCard value={spec.animValue} fast={fast} />;
  if (spec.anim === "wheel") {
    return revealed ? (
      <motion.img src={MASCOT_CHEER} alt="DITHER mascot cheering" data-testid="wheel-mascot-cheer"
        initial={fast ? false : { scale: 0, rotate: -30, y: 30 }} animate={{ scale: 1, rotate: 0, y: 0 }} transition={{ type: "spring", stiffness: 480, damping: 14 }}
        className="animate-bob h-32 w-32 rounded-full border-[4px] border-ink object-cover pop-shadow sm:h-36 sm:w-36" />
    ) : <PrizeWheel labels={spec.reel || [spec.result]} weights={spec.weights} result={spec.result} fast={fast} />;
  }
  return revealed ? null : <SlotReel items={spec.reel?.length ? spec.reel : [spec.result]} />;
}

export function ResultReveal({ spec, onAgain, onClose, againLabel = "Dither again" }: Props) {
  const { fastMode, isFavourite, toggleFavourite, prefs } = useData();
  const sound = !!prefs.sound_effects;
  const [revealed, setRevealed] = useState(false);
  const [share, setShare] = useState<SharePayload | null>(null);

  useEffect(() => {
    if (!spec) return;
    setRevealed(false);
    if (sound && !fastMode && spec.anim === "wheel") sfx.spin(1.25);
    const t = setTimeout(() => {
      setRevealed(true);
      vibrate();
      if (sound) sfx.fanfare();
      if (!fastMode) burst();
    }, fastMode ? 0 : spec.anim === "wheel" ? 1350 : 950);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec?.nonce]);

  useEffect(() => {
    if (!spec) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !share && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [spec, onClose, share]);

  if (!spec) return null;
  const entry = { kind: spec.kind, title: spec.title, result: spec.result, category: spec.category };
  const fav = isFavourite(entry);

  const stageBg = STAGE_BG[spec.anim] || "bg-[#1a1033]";
  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-[#2a1300]/75 backdrop-blur-md" data-testid="result-reveal">
      <div className="flex min-h-full flex-col items-center justify-center px-3 py-8">
        <motion.div key={spec.nonce} initial={fastMode ? false : { scale: 0.88, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 420, damping: 26 }}
          className="relative w-full max-w-md overflow-hidden rounded-[26px] border-[4px] border-[#FFC15E] bg-[#FFF3D6] shadow-2xl">
          <div className={`relative space-y-3 px-4 pb-4 pt-4 ${stageBg}`}>
            <button onClick={onClose} aria-label="Close" data-testid="result-reveal-close"
              className="absolute left-3 top-3 z-10 rounded-full border border-white/40 p-1.5 text-white hover:bg-white/10"><X className="h-4 w-4" strokeWidth={3} /></button>
            <div data-testid="result-reveal-title"><StageHeader title={spec.title} /></div>
            <div className={`flex items-center justify-center ${revealed && spec.anim === "slot" ? "min-h-[3rem]" : "min-h-[11rem]"}`}>
              {revealed && spec.anim === "slot"
                ? <img src={MASCOT_CHEER} alt="" aria-hidden className="h-14 w-14 rounded-full object-cover animate-bob" />
                : <RevealAnimation spec={spec} revealed={revealed} fast={fastMode} />}
            </div>
            <CountdownDots running={!revealed} done={revealed} duration={spec.anim === "wheel" ? 1350 : 950} />
          </div>
          <div className="px-4 pb-5 pt-4">
            {!revealed ? (
              <p className="py-6 text-center font-comic text-2xl text-[#6b3a12] animate-wiggle">{spec.anim === "wheel" ? "Spinning…" : "Thinking…"}</p>
            ) : (
              <motion.div initial={fastMode ? false : { scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 500, damping: 18 }}>
                {spec.podium ? <RafflePodium podium={spec.podium} fast={fastMode} /> : <SaysResult result={spec.result} detail={spec.detail} />}
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <button className="ua-btn flex items-center justify-center gap-1.5 py-2.5 text-sm" onClick={onAgain} data-testid="result-reveal-again-button"><RotateCcw className="h-4 w-4" strokeWidth={3} />{againLabel}</button>
                  <button className="ua-btn flex items-center justify-center gap-1.5 py-2.5 text-sm !border-[#FFE14D] ![background:linear-gradient(180deg,#FFE14D,#FFB000)] !text-[#3b1800] ![text-shadow:none] ![box-shadow:0_4px_0_#B87300]" data-testid="result-reveal-save-button"
                    onClick={() => toast.success(toggleFavourite(entry) ? "Saved to favourites" : "Removed from favourites")}>
                    <Heart className={`h-4 w-4 ${fav ? "fill-pop-pink text-pop-pink" : ""}`} strokeWidth={3} />{fav ? "Saved" : "Save"}
                  </button>
                  <button className="ua-btn flex items-center justify-center gap-1.5 py-2.5 text-sm !border-[#8FE7EE] ![background:linear-gradient(180deg,#3FE0F0,#00B8D4)] ![box-shadow:0_4px_0_#007C91]" data-testid="result-reveal-share-button"
                    onClick={() => setShare({ heading: spec.title, result: spec.result, context: spec.detail })}>
                    <Share2 className="h-4 w-4" strokeWidth={3} />Share
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
      <ShareDialog payload={share} onClose={() => setShare(null)} />
    </div>
  );
}
